import { useMemo, useState, type ReactNode } from 'react'
import { useGlows, useUpdateGlow } from '../../hooks/useGlows'
import { useMe } from '../../hooks/useMe'
import { useEditableField } from '../../hooks/useEditableField'
import { Card } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { CategoryCombobox } from '../../components/ui/CategoryCombobox'
import { ErrorBanner } from '../../components/ErrorBanner'
import { SubscriptionGate } from '../../components/SubscriptionGate'
import { ApiError, apiErrorMessage } from '../../api/client'
import { MAX_CATEGORY_LENGTH, mergeCategorySuggestions } from '../../lib/categories'
import { DeleteGlowDialog } from './DeleteGlowDialog'
import { RotateKeyDialog } from './RotateKeyDialog'
import type { AuthMode, Glow, LitStatus } from '../../api/types'

// SettingsTab edits the mutable fields on a glow. Each field has its
// own Save/Cancel — one PATCH per change keeps the wire shape simple
// (no dirty-form bookkeeping) and matches how owners think about these
// edits ("change just the name" / "flip status to Dark").
//
// Each field is keyed on its current server value (`key={glow.name}`
// etc.) so that when a PATCH lands and the server returns a *normalized*
// value (e.g. a trimmed / lowercased name), the field remounts and its
// `useState(initial)` re-seeds to the canonical value — instead of
// holding the user's pre-normalization text with a stuck dirty=true
// (FE4).
export function SettingsTab({ glow }: { glow: Glow }) {
  return (
    <div className="space-y-4">
      <TextField
        key={glow.title}
        label="Title"
        hint="Optional free-text display name (up to 100 characters). Leave blank for none."
        initial={glow.title}
        field="title"
        glowID={glow.id}
        maxLength={100}
      />

      <CategoryField key={glow.category ?? ''} glow={glow} />

      <SubscriptionGate flag="can_choose_name">
        {({ locked }) => (
          <TextField
            key={glow.name}
            label="Beacon"
            hint="The subdomain label. Lowercase letters, digits, and hyphens. Min 8 chars. Globally unique."
            initial={glow.name}
            disabled={locked}
            disabledHint={
              locked
                ? 'Your current beacon is locked because custom beacons are a paid feature.'
                : undefined
            }
            field="name"
            glowID={glow.id}
          />
        )}
      </SubscriptionGate>

      {glow.status === 'Dark' && glow.dark_reason === 'downgrade' && (
        <DowngradeDarkExplainer glow={glow} />
      )}

      <SelectField
        key={glow.status}
        label="Status"
        hint='"Lit" glows serve traffic. "Dark" returns a 503 and stops serving assets.'
        initial={glow.status}
        options={[
          { value: 'Lit', label: 'Lit (serving)' },
          { value: 'Dark', label: 'Dark (inactive)' },
        ]}
        field="status"
        glowID={glow.id}
      />

      <AuthModeField glow={glow} />

      <HideBrandingToggle glow={glow} />

      <ReadOnlyFields glow={glow} />

      <RotateSigningKeyCard glow={glow} />

      <DangerZoneCard glow={glow} />
    </div>
  )
}

// statusErrorMessage maps an error from a `status` PATCH to a friendly
// inline message. The `lit_limit_reached` (409) case is a CAPACITY signal,
// not the upgrade paywall — the owner already has too many glows lit for
// their plan, so the fix is "darken or delete another", NOT "/billing". It
// is confined to the status path because the server only emits it there.
// `subscription_required` keeps the existing neutral phrasing. `maxGlows`
// is the owner's resolved lit cap (me.subscription.max_glows); a 0/unknown
// cap still renders a sensible sentence (0 → "0 glows lit").
function statusErrorMessage(e: unknown, maxGlows: number): string | null {
  if (e instanceof ApiError && e.code === 'lit_limit_reached') {
    return `You can only have ${maxGlows} ${
      maxGlows === 1 ? 'glow' : 'glows'
    } lit on your plan — darken or delete another glow first.`
  }
  if (e instanceof ApiError && e.code === 'subscription_required') {
    return 'That setting requires a subscription.'
  }
  return null
}

// DowngradeDarkExplainer is shown above the Status field when a glow was
// auto-darkened by a plan downgrade (status Dark + dark_reason 'downgrade').
// It explains WHY the glow went dark and offers a one-click Relight. Relight
// PATCHes status:Lit; the badge/explainer clears from the PATCH response
// (useUpdateGlow.onSuccess writes the fresh glow into the cache and the
// backend clears dark_reason on relight) — never optimistically toggled. A
// re-light that would exceed the plan's lit cap comes back 409
// lit_limit_reached and shows the same darken/delete capacity CTA (not the
// paywall).
function DowngradeDarkExplainer({ glow }: { glow: Glow }) {
  const mut = useUpdateGlow(glow.id)
  const me = useMe()
  const maxGlows = me.data?.subscription.max_glows ?? 0
  const [error, setError] = useState<string | null>(null)

  const relight = async () => {
    setError(null)
    try {
      await mut.mutateAsync({ status: 'Lit' })
      // On success the cache holds the relit glow (status Lit, dark_reason
      // cleared), so this component unmounts — no manual state to reset.
    } catch (e) {
      setError(statusErrorMessage(e, maxGlows) ?? apiErrorMessage(e, 'Could not relight this glow.'))
    }
  }

  return (
    <Card className="border-red-500/30">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-sm font-medium text-red-300">
            Darkened — you&apos;re over your plan&apos;s glow limit
          </h3>
          <p className="mt-0.5 text-xs text-muted">
            This glow was automatically darkened because your account is over
            its plan&apos;s glow limit. Relight it to serve traffic again — or
            upgrade your plan for more lit glows.
          </p>
          {error && <ErrorBanner className="mt-3">{error}</ErrorBanner>}
        </div>
        <Button
          type="button"
          disabled={mut.isPending}
          className="w-full sm:w-auto sm:shrink-0 sm:self-start"
          onClick={relight}
        >
          {mut.isPending ? 'Relighting…' : 'Relight'}
        </Button>
      </div>
    </Card>
  )
}

// DangerZoneCard hosts the irreversible delete. Delete soft-deletes the
// glow (DELETE /v1/glows/{id}): it stops serving immediately and frees a
// glow slot; the files + data are kept for 30 days, then purged, and the
// subdomain name stays reserved until then. To gate the destructive
// action we require the owner to type the glow's `name` (shown as
// "Beacon" in the UI) exactly — stricter than the Versions tab's plain
// confirm because there is no undo.
function DangerZoneCard({ glow }: { glow: Glow }) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  return (
    <>
      <Card className="border-red-500/30">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-sm font-medium text-red-300">Danger zone</h3>
            <p className="mt-0.5 text-xs text-muted">
              Delete this glow. It stops serving immediately and frees a glow
              slot. Files and data are kept for 30 days, then permanently
              deleted. The subdomain name stays reserved until then.{' '}
              <strong>This cannot be undone.</strong>
            </p>
          </div>
          <Button
            variant="danger"
            className="w-full sm:w-auto sm:shrink-0 sm:self-start"
            onClick={() => setConfirmOpen(true)}
          >
            Delete glow
          </Button>
        </div>
      </Card>
      {confirmOpen && (
        <DeleteGlowDialog glow={glow} onClose={() => setConfirmOpen(false)} />
      )}
    </>
  )
}

// RotateSigningKeyCard exposes the forensic per-glow HMAC rotation
// (CLAUDE.md §6). Rotating advances the glow's salt slot, which
// invalidates every active session immediately — Private-glow users
// will be redirected back through magic-link / OIDC signin, Public-glow
// visitors will get a fresh anonymous JWT on their next request. Owners
// should reach for this after a suspected token leak or an
// ownership-transfer-style cleanup.
function RotateSigningKeyCard({ glow }: { glow: Glow }) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  return (
    <>
      <Card>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-sm font-medium text-cream">Signing key</h3>
            <p className="mt-0.5 text-xs text-muted">
              Rotate the per-glow HMAC if you suspect a session token has
              leaked. <strong>This signs out every user on this glow
              immediately</strong> — they&apos;ll need to sign in again on their
              next request.
            </p>
          </div>
          <Button
            variant="danger"
            className="w-full sm:w-auto sm:shrink-0 sm:self-start"
            onClick={() => setConfirmOpen(true)}
          >
            Rotate signing key
          </Button>
        </div>
      </Card>
      {confirmOpen && (
        <RotateKeyDialog glowID={glow.id} onClose={() => setConfirmOpen(false)} />
      )}
    </>
  )
}

// FieldShell wraps each editable field with consistent label/hint/error
// chrome. Children are the input + the save/cancel row.
function FieldShell({
  label,
  hint,
  children,
  error,
}: {
  label: string
  hint?: string | undefined
  children: ReactNode
  error?: string | null
}) {
  return (
    <Card>
      <label className="block text-sm font-medium text-cream">{label}</label>
      {hint && <p className="mt-0.5 text-xs text-muted">{hint}</p>}
      <div className="mt-3">{children}</div>
      {error && <ErrorBanner className="mt-3">{error}</ErrorBanner>}
    </Card>
  )
}

interface TextFieldProps {
  label: string
  hint?: string | undefined
  disabledHint?: string | undefined
  initial: string
  disabled?: boolean
  field: 'name' | 'title'
  glowID: string
  maxLength?: number
}

function TextField({
  label,
  hint,
  disabledHint,
  initial,
  disabled,
  field,
  glowID,
  maxLength,
}: TextFieldProps) {
  const mut = useUpdateGlow(glowID)
  const f = useEditableField(initial, { saving: mut.isPending })

  const save = () =>
    f.save((value) =>
      mut.mutateAsync({ [field]: value } as Parameters<typeof mut.mutateAsync>[0]),
    )

  return (
    <FieldShell label={label} hint={disabled ? disabledHint ?? hint : hint} error={f.error}>
      <div className="flex flex-wrap gap-2 sm:flex-nowrap">
        <Input
          type="text"
          value={f.value}
          disabled={disabled || f.saving}
          onChange={(e) => f.setValue(e.target.value)}
          maxLength={maxLength}
        />
        <Button
          type="button"
          onClick={save}
          disabled={!f.dirty || disabled || f.saving}
        >
          {f.saving ? 'Saving…' : 'Save'}
        </Button>
        {f.dirty && (
          <Button type="button" variant="secondary" onClick={f.cancel}>
            Cancel
          </Button>
        )}
      </div>
    </FieldShell>
  )
}

// CategoryField is the editable Category card. `category` is a FREE field
// (a structural clone of Title — NO SubscriptionGate), so this mirrors
// TextField's edit/save/cancel lifecycle exactly: one PATCH per save, an
// empty value clears it (sends `category:""`). It differs only in rendering
// a CategoryCombobox (defaults + the owner's in-use categories, custom typed
// value allowed) instead of a plain Input — kept out of TextField so that
// component stays text-only. Mounted keyed on `glow.category` at the call
// site, so a PATCH that returns the trimmed/normalized value remounts the
// card and re-seeds the editor to the canonical value (the FE-4 reseed
// pattern, same as the other fields).
function CategoryField({ glow }: { glow: Glow }) {
  const mut = useUpdateGlow(glow.id)
  const f = useEditableField(glow.category ?? '', { saving: mut.isPending })
  const glows = useGlows()

  // Suggestions = defaults + the owner's in-use categories + the value being
  // edited. Degrades to DEFAULT_CATEGORIES while the glows query is loading /
  // errored (`glows.data` undefined) — the combobox still works for typing.
  const suggestions = useMemo(() => {
    const existing = (glows.data ?? [])
      .map((g) => g.category)
      .filter((c): c is string => !!c && c.trim() !== '')
    return mergeCategorySuggestions(existing, f.value)
  }, [glows.data, f.value])

  // An empty editor value naturally PATCHes `category:""` (clear) — no
  // special-casing, exactly like Title.
  const save = () => f.save((value) => mut.mutateAsync({ category: value }))

  return (
    <FieldShell
      label="Category"
      hint="Optional. Group this glow on your dashboard (up to 50 characters). Leave blank for Uncategorized."
      error={f.error}
    >
      <div className="flex flex-wrap gap-2 sm:flex-nowrap">
        <CategoryCombobox
          value={f.value}
          onChange={f.setValue}
          suggestions={suggestions}
          maxLength={MAX_CATEGORY_LENGTH}
          disabled={f.saving}
          aria-label="Category"
        />
        <Button type="button" onClick={save} disabled={!f.dirty || f.saving}>
          {f.saving ? 'Saving…' : 'Save'}
        </Button>
        {f.dirty && (
          <Button type="button" variant="secondary" onClick={f.cancel}>
            Cancel
          </Button>
        )}
      </div>
    </FieldShell>
  )
}

// AuthModeField renders the auth-mode select with Public and Accounts gated
// INDEPENDENTLY (can_set_public vs can_set_accounts) — a single SubscriptionGate
// can't disable the two options on different flags (C2). Private is always
// enabled (downgrades are free); a gated option a free owner isn't on stays
// disabled. The server is the hard boundary; this only mirrors it in the UI.
function AuthModeField({ glow }: { glow: Glow }) {
  const me = useMe()
  const sub = me.isSuccess ? me.data?.subscription : undefined
  const canSetPublic = !!sub?.can_set_public
  const canSetAccounts = !!sub?.can_set_accounts
  return (
    <SelectField
      key={glow.auth_mode}
      label="Auth mode"
      hint="Private requires signin against the allowlist. Accounts lets anyone sign in (open self-serve). Public is open to everyone with no signin."
      initial={glow.auth_mode}
      options={[
        { value: 'Private', label: 'Private (signin required)' },
        {
          value: 'Accounts',
          label: 'Accounts (open self-serve signin)',
          disabled: !canSetAccounts && glow.auth_mode !== 'Accounts',
        },
        {
          value: 'Public',
          label: 'Public (no signin)',
          disabled: !canSetPublic && glow.auth_mode !== 'Public',
        },
      ]}
      field="auth_mode"
      glowID={glow.id}
    />
  )
}

// HideBrandingToggle controls the per-glow paid branding opt-out. A free owner
// cannot enable it; a paid-then-downgraded owner who had it on can still clear
// it (the server gates only false→true).
function HideBrandingToggle({ glow }: { glow: Glow }) {
  const me = useMe()
  const canHide = me.isSuccess && !!me.data?.subscription?.can_hide_branding
  const mut = useUpdateGlow(glow.id)
  const [error, setError] = useState<string | null>(null)
  const locked = !canHide && !glow.hide_branding
  const onToggle = async (checked: boolean) => {
    setError(null)
    try {
      await mut.mutateAsync({ hide_branding: checked })
    } catch (e) {
      if (e instanceof ApiError && e.code === 'subscription_required') {
        setError('Removing Backlit branding requires a subscription.')
        return
      }
      setError(apiErrorMessage(e, 'Could not save.'))
    }
  }
  return (
    <Card>
      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={glow.hide_branding}
          disabled={locked || mut.isPending}
          onChange={(e) => onToggle(e.target.checked)}
          className="mt-1"
        />
        <span>
          <span className="block text-sm font-medium">Hide Backlit branding</span>
          <span className="block text-xs text-muted">
            Removes the floating Backlit button from this glow.
            {locked ? ' Requires a paid plan.' : ''}
          </span>
          {error && <span className="block text-xs text-red-600">{error}</span>}
        </span>
      </label>
    </Card>
  )
}

interface SelectFieldProps {
  label: string
  hint?: string | undefined
  initial: string
  // disabledValue is purely a marker for typing — the disabled state on
  // an option drives the actual UX.
  disabledValue?: string | undefined
  options: Array<{ value: string; label: string; disabled?: boolean }>
  field: 'status' | 'auth_mode'
  glowID: string
}

function SelectField({
  label,
  hint,
  initial,
  options,
  field,
  glowID,
}: SelectFieldProps) {
  const mut = useUpdateGlow(glowID)
  const me = useMe()
  // The owner's resolved lit cap, only meaningful for the status path's
  // lit_limit_reached (409) message.
  const maxGlows = me.data?.subscription.max_glows ?? 0
  const f = useEditableField(initial, { saving: mut.isPending })

  const save = async () => {
    // Pre-empt the generic message with a friendlier one for the gated cases
    // (subscription_required on both paths; lit_limit_reached only on status —
    // a CAPACITY CTA, never the upgrade paywall), then fall back to the
    // generic mapping.
    f.setError(null)
    try {
      const body =
        field === 'status'
          ? { status: f.value as LitStatus }
          : { auth_mode: f.value as AuthMode }
      await mut.mutateAsync(body)
    } catch (e) {
      if (field === 'status') {
        const msg = statusErrorMessage(e, maxGlows)
        if (msg) {
          f.setError(msg)
          return
        }
      } else if (e instanceof ApiError && e.code === 'subscription_required') {
        f.setError('That setting requires a subscription.')
        return
      }
      f.setError(apiErrorMessage(e, 'Could not save.'))
    }
  }

  return (
    <FieldShell label={label} hint={hint} error={f.error}>
      <div className="flex flex-wrap gap-2 sm:flex-nowrap">
        <select
          value={f.value}
          onChange={(e) => f.setValue(e.target.value)}
          disabled={f.saving}
          className="block w-full rounded-md border border-line-strong bg-surface px-3 py-2 text-sm"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
        <Button type="button" onClick={save} disabled={!f.dirty || f.saving}>
          {f.saving ? 'Saving…' : 'Save'}
        </Button>
        {f.dirty && (
          <Button type="button" variant="secondary" onClick={f.cancel}>
            Cancel
          </Button>
        )}
      </div>
    </FieldShell>
  )
}

function ReadOnlyFields({ glow }: { glow: Glow }) {
  return (
    <Card>
      <h3 className="text-sm font-medium text-cream">Read-only</h3>
      <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
        <Pair label="Region" value={glow.region} hint="Pinned at creation" />
        <Pair label="Owner UID" value={glow.owner_uid} mono />
        <Pair label="Live version" value={glow.live_version || '— no deploys yet —'} mono />
        <Pair label="Created" value={fmtDate(glow.created_at)} />
      </dl>
    </Card>
  )
}

function Pair({
  label,
  value,
  mono,
  hint,
}: {
  label: string
  value: string
  mono?: boolean
  hint?: string
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted">{label}</dt>
      <dd
        className={`mt-0.5 truncate ${mono ? 'font-mono text-xs' : 'text-sm'}`}
        title={value}
      >
        {value}
      </dd>
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </div>
  )
}

function fmtDate(iso: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}
